import React, { lazy, Suspense, useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";

import BlogListing from "./BlogListing";

const BlogPost = lazy(() => import("./BlogPost"));

const spreadsheetId = "1AX5TypApTwKzJgVT0qz9OkQ6AKkW8ZQWz_InI7Idtow";
const spreadsheetUrl = `https://docs.google.com/spreadsheets/d/${spreadsheetId}/gviz/tq`;

const normaliseColumnName = column => {
    return column.toLowerCase().replace(/[^a-z0-9]/g, "");
}

const parseCsvList = value => {
    if(!value){
        return [];
    }

    const tags = [];
    const characters = String(value).split("");
    let currentTag = "";
    let inQuotedTag = false;

    for(let index = 0; index < characters.length; index += 1){
        const character = characters[index];

        if(character === '"' && characters[index + 1] === '"'){
            currentTag += character;
            index += 1;
            continue;
        }

        if(character === '"'){
            inQuotedTag = !inQuotedTag;
            continue;
        }

        if(character === "," && !inQuotedTag){
            const tag = currentTag.trim();

            if(tag){
                tags.push(tag);
            }

            currentTag = "";
            continue;
        }

        currentTag += character;
    }

    const finalTag = currentTag.trim();

    if(finalTag){
        tags.push(finalTag);
    }

    return tags.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

const normaliseTag = tag => {
    return tag.trim().toLowerCase();
}

const parseBlogDate = value => {
    if(!value){
        return null;
    }

    const dateParts = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if(dateParts){
        return new Date(Number(dateParts[1]), Number(dateParts[2]) - 1, Number(dateParts[3]));
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
}

const getOrdinalSuffix = day => {
    if(day > 3 && day < 21){
        return "th";
    }

    switch(day % 10){
        case 1:
            return "st";
        case 2:
            return "nd";
        case 3:
            return "rd";
        default:
            return "th";
    }
}

const formatBlogDate = (date, fallback) => {
    if(!date){
        return fallback || "";
    }

    const month = new Intl.DateTimeFormat("en-GB", { month: "long" }).format(date);
    const day = date.getDate();

    return `${day}${getOrdinalSuffix(day)} ${month} ${date.getFullYear()}`;
}

const getDateSortValue = date => {
    return date ? date.getTime() : 0;
}

const parseSpreadsheetResponse = data => {
    let response = data;

    if(typeof data === "string"){
        const match = data.match(/google\.visualization\.Query\.setResponse\(([\s\S]+)\);?$/);

        if(!match){
            throw new Error("Spreadsheet response was not in the expected format");
        }

        response = JSON.parse(match[1]);
    }

    if(response.status !== "ok"){
        throw new Error("Spreadsheet response was not successful");
    }

    const { table } = response;
    const columns = table.cols.map(column => normaliseColumnName(column.label || column.id));

    return table.rows.map(row => {
        return row.c.reduce((post, cell, index) => {
            post[columns[index]] = cell ? cell.f || cell.v : "";
            return post;
        }, {});
    });
}

const loadSpreadsheet = () => {
    return new Promise((resolve, reject) => {
        const callbackName = `spreadsheetCallback${Date.now()}${Math.floor(Math.random() * 100000)}`;
        const script = document.createElement("script");
        let timeoutId;

        const cleanup = () => {
            window.clearTimeout(timeoutId);
            delete window[callbackName];

            if(script.parentNode){
                script.parentNode.removeChild(script);
            }
        }

        window[callbackName] = data => {
            cleanup();
            resolve(data);
        };

        script.onerror = () => {
            cleanup();
            reject(new Error("Unable to load spreadsheet data"));
        };

        timeoutId = window.setTimeout(() => {
            cleanup();
            reject(new Error("Timed out loading spreadsheet data"));
        }, 15000);

        script.src = `${spreadsheetUrl}?tqx=${encodeURIComponent(`out:json;responseHandler:${callbackName}`)}`;
        document.head.appendChild(script);
    });
}

const fetchPosts = () => {
    return loadSpreadsheet()
        .then(data => {
            const rawData = parseSpreadsheetResponse(data);

            if(!Array.isArray(rawData)){
                throw new Error("Raw post data from API not an array");
            }

            if(rawData.length === 0){
                throw new Error("No raw post data found");
            }

            return rawData.map(post => {
                const created = parseBlogDate(post.created);

                return {
                    url: post.url,
                    created,
                    createdLabel: formatBlogDate(created, post.created),
                    updated: parseBlogDate(post.updated),
                    title: post.title,
                    summary: post.summary,
                    content: post.content,
                    status: post.status,
                    tags: parseCsvList(post.tags),
                    hero: post.hero,
                    heroTextColor: post.herotextcolor,
                }
            }).sort((a, b) => {
                return getDateSortValue(b.created) - getDateSortValue(a.created);
            });
        });
}

const Blog = () => {
    const { url } = useParams();
    const { search } = useLocation();
    const [loadingState, setLoadingState] = useState("loading");
    const [posts, setPosts] = useState([]);
    const selectedTag = new URLSearchParams(search).get("tag") || "";

    useEffect(() => {
        fetchPosts().then(posts => {
            setPosts(posts);
            setLoadingState("loaded");
        }).catch(e => {
            setLoadingState("error");
        });
    }, []);

    if(loadingState === "loading"){
        return (
            <>
                <h1>Blog</h1>
                <p className="text-center">Loading...</p>
            </>
        );
    }

    if(loadingState === "loaded") {
        const post = posts.find(post => post.url === url);
        if (post) {
            return (
                <Suspense fallback={<p className="text-center">Loading...</p>}>
                    <BlogPost post={post}/>
                </Suspense>
            );
        }

        const publishedPosts = posts.filter(post => post.status === "published");
        const visiblePosts = selectedTag
            ? publishedPosts.filter(post => post.tags.some(tag => normaliseTag(tag) === normaliseTag(selectedTag)))
            : publishedPosts;

        if (publishedPosts.length) return <BlogListing posts={visiblePosts} selectedTag={selectedTag}/>
    }

    return (
        <>
            <h1>Blog</h1>
            <p className="text-center">Blog content unavailable, please check back later!</p>
        </>
    );
}

export default Blog;
